import Peer, { DataConnection } from 'peerjs'
import React from 'react'

const RETRY_DELAY_MS = 2000
const WAKE_DRIFT_THRESHOLD_MS = 5000
const WAKE_POLL_INTERVAL_MS = 2000

const PEER_CONFIG = {
  config: {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
      { urls: 'stun:stun3.l.google.com:19302' },
    ],
  },
}

const PeerReceiver = ({ peerId }: { peerId: string }) => {
  const [peerConnect, setPeerConnect] = React.useState<DataConnection>()
  const peerRef = React.useRef<Peer>()
  const peerConnectRef = React.useRef<DataConnection>()
  const activeRef = React.useRef(true)

  const updateConnection = React.useCallback(
    (conn: DataConnection | undefined) => {
      peerConnectRef.current = conn
      setPeerConnect(conn)
    },
    []
  )

  const handleVisibilityChange = React.useCallback(() => {
    activeRef.current = document.visibilityState === 'visible'
    if (peerRef?.current && peerConnectRef.current) {
      if (!peerRef.current?.destroyed && !peerConnectRef.current?.open) {
        console.log('Device Peer is attempting to reconnect')
        peerRef.current.reconnect()
      }
    }
  }, [])

  const destroyPeer = React.useCallback(() => {
    if (peerRef?.current) {
      peerRef?.current.destroy()
      console.info('Peer has been destroyed...')
    }
  }, [peerRef])

  React.useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', destroyPeer)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', destroyPeer)
    }
  }, [])

  const attemptConnect = React.useCallback(
    (conn: DataConnection) => {
      if (!conn) {
        console.error('Something went wrong - conn is empty')
        return
      }

      if (!activeRef.current) return

      console.info('Establishing Connection...')

      conn.on('open', () => {
        console.log('A connection has been established to:', peerId)
      })

      conn.on('close', () => {
        console.warn('Connection closed, waiting for sender to reconnect...')
        updateConnection(undefined)
      })

      conn.on('error', (err) => {
        console.error('Connection error:', err.type, err.message)
        updateConnection(undefined)
      })
    },
    [peerId, updateConnection]
  )

  React.useEffect(() => {
    let lastTick = performance.now()
    const driftId = setInterval(() => {
      const now = performance.now()
      const drift = now - lastTick - WAKE_POLL_INTERVAL_MS
      lastTick = now
      if (
        drift > WAKE_DRIFT_THRESHOLD_MS &&
        peerRef.current &&
        !peerRef.current.destroyed
      ) {
        console.warn(`Wake detected, drift: ${drift}ms — reconnecting`)
        peerRef.current.reconnect()
      }
    }, WAKE_POLL_INTERVAL_MS)
    return () => clearInterval(driftId)
  }, [])

  React.useEffect(() => {
    activeRef.current = true
    const peer = new Peer(peerId, PEER_CONFIG)
    peerRef.current = peer
    console.log('A new Peer has been instantiated...')

    peer.on('open', (id) => {
      console.log('Receiver Peer is ready with ID:', id)
      peer.on('connection', (conn) => {
        if (!activeRef.current) return
        updateConnection(conn)
        attemptConnect(conn)
      })
    })

    peer.on('error', (err) => {
      console.error('Peer error:', err.type, err.message)
      if (!peer.destroyed) {
        setTimeout(() => {
          console.log('Device Peer is attempting to reconnect')
          if (activeRef.current) peer.reconnect()
        }, RETRY_DELAY_MS)
      }
    })

    return () => {
      activeRef.current = false
      peerConnectRef.current?.close()
      destroyPeer()
    }
  }, [peerId])

  return { peerConnect, destroyPeer }
}

export default PeerReceiver
